import { createTheme } from '@mui/material/styles';
import type { Shadows } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    surface: {
      level0: string;
      level1: string;
      level2: string;
      level3: string;
    };
    status: {
      active: string;
      completed: string;
      failed: string;
      paused: string;
      pending: string;
      new: string;
    };
    priority: {
      low: string;
      normal: string;
      high: string;
      urgent: string;
    };
    section: {
      headerBg: string;
      headerBorder: string;
      cardBorder: string;
      cardBg: string;
    };
  }
  interface PaletteOptions {
    surface?: {
      level0: string;
      level1: string;
      level2: string;
      level3: string;
    };
    status?: {
      active: string;
      completed: string;
      failed: string;
      paused: string;
      pending: string;
      new: string;
    };
    priority?: {
      low: string;
      normal: string;
      high: string;
      urgent: string;
    };
    section?: {
      headerBg: string;
      headerBorder: string;
      cardBorder: string;
      cardBg: string;
    };
  }
}

// Design System 2.0 (D-009): panels, cards, menus and popovers sit on the 6px
// panel corner; controls keep the 4px shape below.
const basePaper = { styleOverrides: { root: { backgroundImage: 'none' as const }, rounded: { borderRadius: 6 } } };

// Focus ring backstop. The global `:focus-visible` rule in src/index.css
// already paints this; this override exists so MUI's default `outline: none`
// reset cannot suppress it on any ButtonBase descendant (Button, IconButton,
// ListItemButton, etc.). Design System 2.0 (D-010): the one focus ring is a
// 3px indigo outline offset 2px (it was paprika at 4px) — indigo is structure
// and actions; paprika is attention only.
// R4.2 Part B item 8: skip .MuiTab-root via :not() so Tabs use their native
// underline indicator instead of the offset outline (which renders as
// strips flanking the tab — the bug Cowork flagged).
const baseButtonBase = {
  styleOverrides: {
    root: {
      '&:focus-visible:not(.MuiTab-root)': {
        outline: '3px solid var(--indigo)',
        outlineOffset: '2px',
      },
    },
  },
};

// R4.5: badge styling moved to the generic `badge` slot via a function-form
// override that reads ownerState.color. R3.1's narrow colorDefault/colorPrimary
// keys only matched when MUI actually emitted a MuiBadge-color* class — but the
// left-nav badges (Personal Inbox 87, Help Desk 99+, etc.) shipped no color
// class at all (DOM showed just `MuiBadge-badge MuiBadge-standard
// MuiBadge-anchorOriginTopRight*`), so the previous override never matched and
// those badges rendered without a background. The function-form generic-slot
// override fires regardless of what color class MUI emits. Semantic colors
// (error / success / warning / info / secondary) are excluded so they keep
// their MUI-derived palette values.
// Design System 2.0: a count badge is paprika with its on- ink (D-043 — white
// on day paprika is 3.1:1, so it is carbon there), 11/700, on the 3px tag
// corner (D-009). A `dot` badge stays round: status dots are round.
const baseBadge = {
  styleOverrides: {
    badge: ({ ownerState }: { ownerState: { color?: string } }) => (
      ownerState.color !== 'error' &&
      ownerState.color !== 'success' &&
      ownerState.color !== 'warning' &&
      ownerState.color !== 'info' &&
      ownerState.color !== 'secondary'
        ? { backgroundColor: 'var(--paprika)', color: 'var(--on-paprika)' }
        : {}
    ),
    standard: { borderRadius: 3, fontSize: 11, fontWeight: 700 },
  },
};

// R4.5: every <Icon color="primary" /> / <Icon color="secondary" /> in the app
// (left-nav icons, page-title icons, button-internal icons) used MUI's default
// SvgIcon override which paints palette.primary.main / .secondary.main at full
// strength — i.e., literal #0443ef indigo or #f76100 paprika regardless of
// mode. On lifted-dark #5C5C5C paper, deep #0443ef fails contrast. R4.3 added
// equivalent overrides for MuiButton.outlinedPrimary, textPrimary, MuiTab, and
// MuiChip.outlinedPrimary — MuiSvgIcon was the missing peer. Routing through
// --indigo-on-soft / --paprika-on-soft picks up the lighter mode-aware variant
// (#B0C7FF / #FFC096 in dark) so icons stay visible without per-icon edits.
const baseSvgIcon = {
  styleOverrides: {
    colorPrimary: {
      color: 'var(--indigo-on-soft)',
    },
    colorSecondary: {
      color: 'var(--paprika-on-soft)',
    },
  },
};

// R4.2: default (color="primary") LinearProgress bars used theme.palette.primary
// .main = #0443ef indigo, which barely showed against the lifted-dark #5C5C5C
// paper (process instance progress, revenue-by-client progress, etc.), so the
// bar was routed through paprika. Design System 2.0: a meter is indigo (D-017
// B16) — paprika is attention, one per region — and the night --indigo is
// bright enough on the night paper now. The track reads --progress-track.
// Scoped to colorPrimary so color="error" / "warning" / "success" /
// "secondary" bars keep their semantic palettes.
const baseLinearProgress = {
  styleOverrides: {
    colorPrimary: {
      backgroundColor: 'var(--progress-track)',
      '& .MuiLinearProgress-bar': {
        backgroundColor: 'var(--indigo)',
      },
    },
  },
};

// R4.3 Part A: outlined and text Button variants route through the mode-aware
// soft/on-soft token pair instead of theme.palette.primary.main. MUI's defaults
// paint the literal indigo #0443ef which fails contrast on lifted-dark #5C5C5C
// paper. By reading var(--indigo-on-soft) we get #0443ef in light, #B0C7FF in
// dark, and #0337c5 in sepia. Same for the secondary (paprika) family.
// Design System 2.0: buttons are Inter Tight 700 (D-006, from the button
// type style) and never carry an elevation shadow — no shadow at rest (D-010).
const baseButton = {
  defaultProps: { disableElevation: true },
  styleOverrides: {
    outlinedPrimary: {
      color: 'var(--indigo-on-soft)',
      borderColor: 'var(--indigo-on-soft)',
      '&:hover': {
        backgroundColor: 'var(--indigo-soft)',
        borderColor: 'var(--indigo-on-soft)',
      },
    },
    textPrimary: {
      color: 'var(--indigo-on-soft)',
      '&:hover': {
        backgroundColor: 'var(--indigo-soft)',
      },
    },
    outlinedSecondary: {
      color: 'var(--paprika-on-soft)',
      borderColor: 'var(--paprika-on-soft)',
      '&:hover': {
        backgroundColor: 'var(--paprika-soft)',
        borderColor: 'var(--paprika-on-soft)',
      },
    },
    textSecondary: {
      color: 'var(--paprika-on-soft)',
      '&:hover': {
        backgroundColor: 'var(--paprika-soft)',
      },
    },
  },
};

// R4.3 Part B: selected Tab text and the Tabs underline indicator both read
// from --indigo-on-soft so the active-tab signal stays legible on lifted dark.
// In light/sepia this is the same brand indigo as before.
// R4.4 Part E2: inactive tab color promoted to text.primary so the
// Closed/Snoozed/Unread tabs in Help Desk (and every other Tabs row) read
// at full contrast on lifted-dark #5C5C5C paper. MUI's default was
// text.secondary (#C8C8C8 in dark) which faded at small font sizes.
// Design System 2.0 (D-017 B11): tab labels are Inter Tight 13.5 / 600.
const baseTab = {
  styleOverrides: {
    root: {
      color: 'var(--header-text)',
      fontSize: '13.5px',
      fontWeight: 600,
      '&.Mui-selected': {
        color: 'var(--indigo-on-soft)',
      },
    },
  },
};
const baseTabs = {
  styleOverrides: {
    indicator: {
      backgroundColor: 'var(--indigo-on-soft)',
    },
  },
};

// R4.1: <Chip color="primary" variant="outlined" /> chips (inbox thread tags,
// ConversationView client tags, ClientInboxList open-count chips, etc.) used
// MUI's default primary.main = #0443ef indigo for both border and text. On
// lifted dark's #5C5C5C paper, #0443ef text gets ~3:1 contrast and fails WCAG
// AA for small text. Routing color + border through var(--indigo-on-soft)
// gives us the mode-appropriate variant: #0443ef in light, #B0C7FF in dark,
// #0337c5 in sepia. Backgrounds stay transparent (variant="outlined"
// invariant). Scoped to the outlinedPrimary slot only — colorError /
// colorWarning / etc. chips keep their semantic palette.
// Design System 2.0: chips sit on the 3px tag corner (D-009), labels 12.5 / 600
// (11 on a small chip) — on the type scale (D-005).
const baseChip = {
  styleOverrides: {
    root: { borderRadius: 3, fontSize: '12.5px', fontWeight: 600 },
    sizeSmall: { fontSize: '11px' },
    outlinedPrimary: {
      color: 'var(--indigo-on-soft)',
      borderColor: 'var(--indigo-on-soft)',
    },
  },
};

// R4.6: <ToggleButton selected> (e.g. the inbox Open/Closed/Snoozed/Unread
// filter group) painted the selected-state text in palette.primary.main =
// #0443ef indigo regardless of mode — fails contrast on lifted-dark #5C5C5C
// paper. R4.3's MuiTab override doesn't reach this component family because
// the inbox filter uses ToggleButtonGroup, not Tabs. Routing the selected
// color through --indigo-on-soft picks up the lighter #B0C7FF in dark while
// keeping brand indigo in light/sepia. Note: a consumer sx override on the
// parent ToggleButtonGroup is one selector level deeper and will still win
// if it sets color explicitly — that consumer is migrated in Part C.
const baseToggleButton = {
  styleOverrides: {
    root: {
      '&.Mui-selected': {
        color: 'var(--indigo-on-soft)',
      },
    },
  },
};

// Brand font stacks — headline = Inter Tight, body = Inter. Loaded from Google Fonts
// in frontend/public/index.html. Stacks are duplicated here (vs. reading the
// --font-head / --font-body CSS vars) so MUI's typography sizing engine has a
// concrete font-family string to compute against at theme build time.
const FONT_HEAD = '"Inter Tight", "Acumin Variable Concept", "Codec Cold Trial", "Roboto Condensed", "Helvetica Neue", Arial, sans-serif';
const FONT_BODY = '"Inter", "Manrope", "Satoshi", "Helvetica Neue", Arial, sans-serif';

// Design System 2.0 (D-005 sizes, D-006 weights): every MUI type variant sits
// on the 13-step scale. MUI's defaults (96 / 60 / 48 / 34 / 24 / 20 heads,
// 16px body, 12px caption, weight 300) were all off it. The mapping (Claude's
// craft call under D-016, recorded in decisions.md):
//   h1 display 88 · h2 big figure 56 · h3 hero 40 · h4 section / page title 30
//   · h5 tile 22 · h6 panel title 20 — Inter Tight 800
//   subtitle1 14 / 600 · subtitle2 13.5 / 600 · body1 body 14 · body2 sub 13.5
//   · caption small 12.5 · button 13.5 / 700 · overline the micro voice
//   (10.5 / 800 / .1em, uppercase). The unloaded 300 / 550 / 650 / 750 retire.
const baseTypography = {
  fontFamily: FONT_BODY,
  fontWeightLight: 400,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { fontFamily: FONT_HEAD, fontSize: '88px', fontWeight: 800, lineHeight: 0.9, letterSpacing: '-0.045em' },
  h2: { fontFamily: FONT_HEAD, fontSize: '56px', fontWeight: 800, lineHeight: 0.88, letterSpacing: '-0.045em' },
  h3: { fontFamily: FONT_HEAD, fontSize: '40px', fontWeight: 800, lineHeight: 1.02, letterSpacing: '-0.03em' },
  h4: { fontFamily: FONT_HEAD, fontSize: '30px', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.025em' },
  h5: { fontFamily: FONT_HEAD, fontSize: '22px', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' },
  h6: { fontFamily: FONT_HEAD, fontSize: '20px', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.015em' },
  subtitle1: { fontSize: '14px', fontWeight: 600, lineHeight: 1.5 },
  subtitle2: { fontSize: '13.5px', fontWeight: 600, lineHeight: 1.5 },
  body1: { fontSize: '14px', lineHeight: 1.55 },
  body2: { fontSize: '13.5px', lineHeight: 1.5 },
  caption: { fontSize: '12.5px', lineHeight: 1.45 },
  button: { fontFamily: FONT_HEAD, fontSize: '13.5px', fontWeight: 700, textTransform: 'none' as const },
  overline: { fontFamily: FONT_HEAD, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.1em', lineHeight: 2 },
};

// Shared shape token — 4px default radius per the design system's geometry
// law (06-design-system.md §2; DS-001 resolved in DES-W1). The old 8px here
// claimed "brand spec" but was the OLD spec — HubThemeScope had been
// correcting it to 4px inside designed regions; now the base agrees.
// Design System 2.0 (D-009): 4 is the control corner (buttons, fields); the
// paper overrides above and below take panels, menus and modals to 6 and tags
// to 3.
const baseShape = { borderRadius: 4 };

// Design System 2.0 (D-010): MUI's 25 elevations map onto the six shadows.
// None at rest: elevation 1 (every resting Paper and Card) and 0 draw nothing.
// 2–4 are the hover lifts code asks for (`'&:hover': { boxShadow: 3 }`) → lift.
// 5–12 are menus, popovers, pickers and raised panels → pop. 13–24 are
// drawers and dialogs → modal. Toasts read --toast directly (SnackbarContent).
const baseShadows = Array.from({ length: 25 }, (_, i) => (
  i <= 1 ? 'none' : i <= 4 ? 'var(--lift)' : i <= 12 ? 'var(--pop)' : 'var(--modal)'
)) as Shadows;

// Design System 2.0 (D-011): MUI's Material curves and 150–375ms durations
// move onto the system's motion — state changes 120ms, a lift / a modal / a
// drawer 250ms, all on --ease-out. Collapse's auto height duration is MUI's own.
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
const baseTransitions = {
  easing: { easeInOut: EASE_OUT, easeOut: EASE_OUT, easeIn: 'cubic-bezier(0.4, 0, 1, 1)', sharp: EASE_OUT },
  duration: { shortest: 120, shorter: 120, short: 120, standard: 250, complex: 250, enteringScreen: 250, leavingScreen: 120 },
};

// Design System 2.0 (D-017 B4): a field is 40 tall — the 13.5 value on its
// 1.4375em line plus 10.3px above and below, in both MUI sizes (the app's
// standard field is `size="small"`, already 40 at the old 16px font). The
// label (13.5) centres in it until it floats; the floated label and its notch
// read at .8 so they stay on the scale's 10.5 floor. Focus is the indigo frame
// plus the 3px soft halo (D-010), not MUI's 2px border; the placeholder reads
// --ink-faint (D-043). MUI still hides a placeholder under an un-floated label.
const FIELD_PAD_Y = '10.3px';
const baseInputBase = {
  styleOverrides: {
    root: { fontSize: '13.5px' },
    input: {
      '&::placeholder': { color: 'var(--ink-faint)', opacity: 1 },
    },
  },
};
const baseOutlinedInput = {
  styleOverrides: {
    root: {
      borderRadius: 4,
      transition: 'box-shadow 120ms cubic-bezier(0.16, 1, 0.3, 1)',
      '&.Mui-focused': { boxShadow: 'var(--focus-field)' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--indigo)', borderWidth: 1 },
      '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--paprika)' },
    },
    // Vertical padding only: MUI's 14px sides and its adornment / multiline rules stay its own.
    input: {
      paddingTop: FIELD_PAD_Y, paddingBottom: FIELD_PAD_Y,
      '&.MuiInputBase-inputMultiline': { paddingTop: 0, paddingBottom: 0 },
    },
    inputSizeSmall: { paddingTop: FIELD_PAD_Y, paddingBottom: FIELD_PAD_Y },
    multiline: { paddingTop: FIELD_PAD_Y, paddingBottom: FIELD_PAD_Y },
    notchedOutline: { '& legend': { fontSize: '0.8em' } },
  },
};
const baseInputLabel = {
  styleOverrides: {
    root: { fontSize: '13.5px' },
    outlined: {
      transform: 'translate(14px, 10.3px) scale(1)',
      '&.MuiInputLabel-sizeSmall': { transform: 'translate(14px, 10.3px) scale(1)' },
      '&.MuiInputLabel-shrink': { transform: 'translate(14px, -8px) scale(0.8)' },
    },
  },
};
const baseFormHelperText = { styleOverrides: { root: { fontSize: '11px' } } };
const baseFormControlLabel = { styleOverrides: { label: { fontSize: '13.5px' } } };
// An Autocomplete's own paddings put it at 52 / 36 on the 13.5 font; these
// bring both sizes to the one 40 field.
const baseAutocomplete = {
  styleOverrides: {
    inputRoot: {
      '&.MuiOutlinedInput-root': { paddingTop: 6, paddingBottom: 6 },
      '&.MuiOutlinedInput-root .MuiAutocomplete-input': { paddingTop: '4.3px', paddingBottom: '4.3px' },
    },
    paper: { borderRadius: 6, border: '1px solid var(--rule-strong)', boxShadow: 'var(--pop)' },
    option: { fontSize: '12.5px', fontWeight: 600, '&.Mui-focused': { backgroundColor: 'var(--hover)' } },
  },
};

// Design System 2.0 (D-017 B30, D-010): one menu for row menus, select lists
// and pickers — the 6px panel corner, a 1px --rule-strong frame, the pop
// shadow, items 12.5 / 600. A bare Popover takes the corner and shadow only:
// its content often draws its own frame.
const baseMenu = {
  styleOverrides: {
    paper: { borderRadius: 6, border: '1px solid var(--rule-strong)', boxShadow: 'var(--pop)' },
  },
};
// D-053: the hover is the mode-aware --hover shade (MUI's 4% grey all but vanished, and
// sank in Night). Select lists use MenuItem too, where indigo already means picked, so
// this hover stays neutral; HubMenu's action menus take the indigo tint.
const baseMenuItem = { styleOverrides: { root: { fontSize: '12.5px', fontWeight: 600, '&:hover': { backgroundColor: 'var(--hover)' } } } };
const baseTableRow = { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: 'var(--hover)' } } } };
const basePopover = { styleOverrides: { paper: { borderRadius: 6, boxShadow: 'var(--pop)' } } };

// Design System 2.0 (D-017 B14, C22): the tooltip is carbon with on-carbon
// words at 12.5, a 4px corner, the pop shadow, at most 260 wide, and appears
// after 400ms, every time — moving straight on to a neighbour's tooltip waits
// too (the published HubTooltip page; HubTooltip sets the same pair).
const baseTooltip = {
  defaultProps: { enterDelay: 400, enterNextDelay: 400 },
  styleOverrides: {
    tooltip: {
      backgroundColor: 'var(--carbon)', color: 'var(--on-carbon)',
      fontFamily: FONT_BODY, fontSize: '12.5px', fontWeight: 400, lineHeight: 1.45,
      padding: '6px 9px', borderRadius: 4, maxWidth: 260, boxShadow: 'var(--pop)',
    },
    arrow: { color: 'var(--carbon)' },
  },
};

// Design System 2.0: a banner's corner is 4 (D-020); a toast takes the toast
// shadow (D-010); a table cell reads the sub step (13.5, D-005).
const baseAlert = { styleOverrides: { root: { borderRadius: 4 } } };
const baseSnackbarContent = { styleOverrides: { root: { boxShadow: 'var(--toast)' } } };
const baseTableCell = { styleOverrides: { root: { fontSize: '13.5px' } } };

// DS-001: modal paper is 6px (cards/buttons/inputs 4px, StatusTags 0px).
// Matches the HubThemeScope dialog override so scoped and unscoped dialogs
// carry the same geometry.
// PQR-005 (2026-10-07): a dialog does not take the caret back from a window
// opened on top of it. MUI's default ("enforce focus") pulls focus into the
// dialog whenever it lands outside the dialog's own React tree — and the
// app's record windows (DetailModalShell) and its email composer are
// hand-portaled to <body>, usually mounted BESIDE the dialog that opened
// them. The result was a composer nobody could type in from the Call
// Crusher, and a project nobody could edit from a sprint. It was patched per
// screen twice; it is a default now so a new dialog cannot bring it back.
// Opening still moves focus into the dialog and Tab still stays inside it.
const dialogFocus = { disableEnforceFocus: true };
const baseDialog = { defaultProps: dialogFocus, styleOverrides: { paper: { borderRadius: 6 } } };

// DES-W1: theme.palette.status / theme.palette.priority re-point at the
// semantic status tokens (cssVariables.ts) so they follow day/sepia/night
// instead of freezing raw Material hexes (the pre-DES-W1 values). The
// custom palette slots are passed through to CSS verbatim (no MUI color
// math runs on them — verified: the only consumers, tableTheme.ts
// getStatusColor/getPriorityColor, return them as plain CSS colors), so
// var() references are safe. pending/new map to the neutral family: the
// semantic set has no "info blue"; informational/not-yet states are
// neutral slate per the AUD-112 status vocabulary.
const baseStatus = {
  active: 'var(--status-on-track)',
  completed: 'var(--status-on-track)',
  failed: 'var(--status-off-track)',
  paused: 'var(--status-warning)',
  pending: 'var(--status-neutral)',
  new: 'var(--status-neutral)',
};
const basePriority = {
  low: 'var(--status-on-track)',
  normal: 'var(--status-neutral)',
  high: 'var(--status-warning)',
  urgent: 'var(--status-off-track)',
};

// The component overrides every mode shares (the per-mode AppBar and Card
// hairline are added below). HubThemeScope layers the hub grammar on top.
const baseComponents = {
  MuiPaper: basePaper,
  MuiButtonBase: baseButtonBase,
  MuiBadge: baseBadge,
  MuiSvgIcon: baseSvgIcon,
  MuiChip: baseChip,
  MuiLinearProgress: baseLinearProgress,
  // contrastText was previously hardcoded as #0A0A0A which paired with the old
  // gold primary. Black-on-indigo failed WCAG; we drop the literal so MUI
  // derives the foreground from palette.primary.contrastText.
  MuiButton: baseButton,
  MuiTab: baseTab,
  MuiTabs: baseTabs,
  MuiToggleButton: baseToggleButton,
  MuiDialog: baseDialog,
  MuiInputBase: baseInputBase,
  MuiOutlinedInput: baseOutlinedInput,
  MuiInputLabel: baseInputLabel,
  MuiFormHelperText: baseFormHelperText,
  MuiFormControlLabel: baseFormControlLabel,
  MuiAutocomplete: baseAutocomplete,
  MuiMenu: baseMenu,
  MuiMenuItem: baseMenuItem,
  MuiTableRow: baseTableRow,
  MuiPopover: basePopover,
  MuiTooltip: baseTooltip,
  MuiAlert: baseAlert,
  MuiSnackbarContent: baseSnackbarContent,
  MuiTableCell: baseTableCell,
};
const cardOverride = (hairline: string) => ({
  styleOverrides: { root: { backgroundImage: 'none', border: `1px solid ${hairline}`, borderRadius: 6 } },
});

// R3.2: theme mode is a three-value enum. Light + Sepia are derived from each
// other (sepia is light with cream backgrounds + warm-dark text + bumped
// soft-token opacities). Dark is the R3.1 lifted dark stack.
//
// Design System 2.0: each palette reads the mode's own token values
// (tokens.json, theme/cssVariables.ts) so MUI chrome and tokenized chrome
// agree — primary is the mode's --indigo (hover --indigo-ink), secondary its
// --paprika, the semantic colours its --status-* set, text.secondary its
// --ink-soft. contrastText is each fill's on- ink (D-043): white on indigo in
// day and sepia, carbon in night; carbon on day and night paprika.
export type ThemeMode = 'light' | 'sepia' | 'dark';

export function buildTheme(mode: ThemeMode) {
  if (mode === 'dark') {
    return createTheme({
      typography: baseTypography,
      shape: baseShape,
      shadows: baseShadows,
      transitions: baseTransitions,
      palette: {
        mode: 'dark',
        // DES-W1 (DS-003): the R3.1 lifted-gray dialect (#4F4F4F/#5C5C5C/
        // #666666/#707070) is retired. The MUI dark palette now sits on the
        // same night ladder as the cssVariables token layer (bg #121316 <
        // paper-2 #191B1F < paper #1F2126 < lift #22252B) so MUI-styled
        // chrome and tokenized chrome stop disagreeing. One dark mode.
        // 2.0: primary was brand #0443ef here, which read ~2.4:1 as text on
        // the night paper; the night --indigo reads 4.8:1, and its label is
        // carbon (white on it is 3.3:1).
        background: { default: '#121316', paper: '#1F2126' },
        primary:   { main: '#5B86FF', light: 'rgba(91, 134, 255, 0.24)', dark: '#8FA9FF', contrastText: '#16181B' },
        secondary: { main: '#FF7D33', light: 'rgba(255, 125, 51, 0.22)', dark: '#FFA468', contrastText: '#16181B' },
        info:    { main: '#29b6f6' },
        error:   { main: '#FF6259' },
        warning: { main: '#FFA726' },
        success: { main: '#6FCB74' },
        text:    { primary: '#F4F5F7', secondary: '#C6CAD1' },
        divider: 'rgba(244, 245, 247, 0.15)',
        surface: {
          level0: '#121316',
          level1: '#191B1F',
          level2: '#1F2126',
          level3: '#22252B',
        },
        status: baseStatus,
        priority: basePriority,
        section: {
          headerBg: 'rgba(244, 245, 247, 0.06)',
          headerBorder: 'rgba(244, 245, 247, 0.15)',
          cardBorder: 'rgba(244, 245, 247, 0.15)',
          cardBg: '#1F2126',
        },
      },
      components: {
        ...baseComponents,
        MuiAppBar: { styleOverrides: { root: { backgroundColor: '#191B1F', color: '#F4F5F7' } } },
        MuiCard: cardOverride('rgba(244, 245, 247, 0.15)'),
      },
    });
  }

  if (mode === 'sepia') {
    // R3.2: sepia mode — cream paper, warm-dark text. Brand accents (indigo +
    // paprika) are inherited from light mode unchanged; only neutrals and the
    // text-on-tint tokens shift. CSS-variable layer (theme/cssVariables.ts)
    // carries the matching --indigo-soft / --paprika-soft / --*-on-soft bumps.
    // 2.0: primary and secondary move to sepia's own deeper --indigo /
    // --paprika, and text.secondary from --ink-faint to --ink-soft.
    return createTheme({
      typography: baseTypography,
      shape: baseShape,
      shadows: baseShadows,
      transitions: baseTransitions,
      palette: {
        mode: 'light', // MUI palette mode stays 'light' so derived shadows/etc. work; brand reads sepia from cssVariables + section tokens.
        // DES-W1: neutrals aligned to the sepia Revised token ladder
        // (parchment page #E7DCC2 < alt #F2EAD6 < ivory paper #FCF7EB) so
        // MUI chrome agrees with the token layer. The old background.default
        // #F1E9D6 didn't even match the old token #F0E8D2 — that drift is
        // exactly what this alignment retires.
        background: { default: '#E7DCC2', paper: '#FCF7EB' },
        primary:   { main: '#0337C5', light: 'rgba(4, 67, 239, 0.24)', dark: '#022694', contrastText: '#FFFFFF' },
        secondary: { main: '#B84D00', light: 'rgba(247, 97, 0, 0.24)', dark: '#8C3A00', contrastText: '#FFFFFF' },
        info:    { main: '#0288d1' },
        error:   { main: '#C62828' },
        warning: { main: '#D96400' },
        success: { main: '#2E7D32' },
        text:    { primary: '#2C2114', secondary: '#4E402B' },
        divider: 'rgba(56, 42, 26, 0.17)',
        surface: {
          level0: '#E7DCC2',
          level1: '#F2EAD6',
          level2: '#FCF7EB',
          level3: '#FCF7EB',
        },
        status: baseStatus,
        priority: basePriority,
        section: {
          headerBg: 'rgba(56, 42, 26, 0.05)',
          headerBorder: 'rgba(56, 42, 26, 0.17)',
          cardBorder: 'rgba(56, 42, 26, 0.17)',
          cardBg: '#FCF7EB',
        },
      },
      components: {
        ...baseComponents,
        MuiAppBar: { styleOverrides: { root: { backgroundColor: '#FCF7EB', color: '#2C2114', borderBottom: '1px solid rgba(56, 42, 26, 0.17)' } } },
        MuiCard: cardOverride('rgba(56, 42, 26, 0.17)'),
      },
    });
  }

  // Fallthrough: light mode (the default).
  return createTheme({
    typography: baseTypography,
    shape: baseShape,
    shadows: baseShadows,
    transitions: baseTransitions,
    palette: {
      mode: 'light',
      // DES-W1: neutrals aligned to the day Revised token ladder (page
      // #E8E9EC < alt #F4F5F7 < paper #FFFFFF) — white becomes the content
      // surface, not the wash. Divider tracks the new --rule strength.
      background: { default: '#E8E9EC', paper: '#ffffff' },
      primary:   { main: '#0443EF', light: 'rgba(4, 67, 239, 0.24)', dark: '#0337C2', contrastText: '#FFFFFF' },
      secondary: { main: '#F76100', light: 'rgba(247, 97, 0, 0.24)', dark: '#d65500', contrastText: '#16181B' },
      info:    { main: '#0288d1' },
      error:   { main: '#D32F2F' },
      warning: { main: '#ED6C02' },
      success: { main: '#2E7D32' },
      text:    { primary: '#16181B', secondary: '#3F444B' },
      divider: 'rgba(22, 24, 27, 0.13)',
      surface: {
        level0: '#E8E9EC',
        level1: '#F4F5F7',
        level2: '#ffffff',
        level3: '#ffffff',
      },
      status: baseStatus,
      priority: basePriority,
      section: {
        headerBg: 'rgba(22, 24, 27, 0.03)',
        headerBorder: 'rgba(22, 24, 27, 0.13)',
        cardBorder: 'rgba(22, 24, 27, 0.13)',
        cardBg: '#ffffff',
      },
    },
    components: {
      ...baseComponents,
      MuiAppBar: { styleOverrides: { root: { backgroundColor: '#FFFFFF', color: '#16181B', borderBottom: '1px solid rgba(22, 24, 27, 0.13)' } } },
      MuiCard: cardOverride('rgba(22, 24, 27, 0.13)'),
    },
  });
}
